import React from 'react';
import { useMemo, useRef, useState } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAlert } from 'react-alert';
import TurnstileWidget from './TurnstileWidget';

/* Vite inyecta estas variables en tiempo de build (ver .env.example).
   Sin VITE_TURNSTILE_SITE_KEY el formulario se comporta como antes: sin
   verificación y sin token en el payload. */
const ENDPOINT =
  import.meta.env.VITE_CONTACT_ENDPOINT ||
  'https://biodynamics.tech/api/email/send';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

/* Validaciones deliberadamente laxas: atajan el typo, no intentan decidir si
   una dirección existe. El filtro real lo hace el backend al enviar. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[\d][\d\s()-]{6,19}$/;

/* La API espera estos campos siempre. En la variante corta no se piden, así
   que viajan con el mismo relleno que usaba el formulario original.
   `options` corresponde a una lista de checkboxes que está comentada en el
   JSX desde hace tiempo: se mantiene en el payload para no cambiarle la forma
   al endpoint, que no vive en este repo. */
const FILLER = {
  last_name: 'no_last_name',
  job: 'lead',
  phone: '123456789',
  str_options: 'no_data',
  options: [false, false, false, false, false, false],
};

const Spinner = function Spinner() {
  return (
    <span
      className="spinner-grow spinner-grow-sm mr-3"
      role="status"
      aria-hidden="true"
    ></span>
  );
};

const Send = function Send() {
  return (
    <span className="text-white pr-3">
      <i className="fas fa-paper-plane" />
    </span>
  );
};

const ContactForm = function ContactForm(props) {
  const [t] = useTranslation('common');
  const alert = useAlert();

  /* `variant` elige qué campos se piden. Los consumidores que no la pasan
     (los themes, FAQ, el resto de los productos) siguen con el formulario
     de siempre: nombre, email y mensaje. */
  const is_satty = props.data.variant === 'satty';

  const industries = useMemo(() => {
    const value = t('contact.satty.industries', { returnObjects: true });
    return Array.isArray(value) ? value : [];
  }, [t]);

  const placeholder = {
    name: t('contact.form.name'),
    email: t('contact.form.email'),
    phone: is_satty ? t('contact.satty.phone') : t('contact.form.phone'),
    industry: t('contact.satty.industry_placeholder'),
    msg: is_satty ? t('contact.satty.msg') : t('contact.form.msg'),
    sent: t('contact.form.sent'),
    button: is_satty ? t('contact.satty.button') : t('contact.form.button'),
  };

  const form_ref = useRef(null);
  const turnstile_ref = useRef(null);

  const [sending, setSending] = useState(false);
  const [touched, setTouched] = useState({});
  const [captcha_token, setCaptchaToken] = useState('');
  const [captcha_failed, setCaptchaFailed] = useState(false);
  const [state, setState] = useState({
    name: '',
    email: '',
    phone: '',
    industry: '',
    message: '',
  });

  /* Los errores se derivan del estado en cada render. La versión anterior los
     calculaba dentro de un useEffect sin dependencias que corría en todos los
     renders y escribía estado: misma información, un ciclo de más. */
  const errors = useMemo(() => {
    const found = {};
    if (!state.name.trim()) found.name = t('contact.errors.name');
    if (!EMAIL_RE.test(state.email.trim())) found.email = t('contact.errors.email');
    if (!state.message.trim()) found.message = t('contact.errors.message');
    if (is_satty) {
      if (!PHONE_RE.test(state.phone.trim())) found.phone = t('contact.errors.phone');
      if (!state.industry) found.industry = t('contact.errors.industry');
    }
    return found;
  }, [state, is_satty, t]);

  const needs_captcha = Boolean(TURNSTILE_SITE_KEY);
  const captcha_ready = !needs_captcha || Boolean(captcha_token);
  const has_errors = Object.keys(errors).length > 0;
  const is_disabled = sending || has_errors || !captcha_ready;

  const changeHandler = (event) => {
    const { name, value } = event.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  /* El error aparece recién cuando el visitante sale del campo: marcarlo en
     rojo mientras todavía está escribiendo el correo es ruido. */
  const blurHandler = (event) => {
    const { name } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const fieldClass = (name) =>
    'form-control' + (touched[name] && errors[name] ? ' is-invalid' : '');

  const fieldError = (name) =>
    touched[name] && errors[name] ? (
      <small className="text-danger d-block text-left mt-1">{errors[name]}</small>
    ) : null;

  const submitHandler = async (event) => {
    /* El original lo tenía comentado, así que el navegador navegaba al hacer
       submit y cortaba el POST a mitad de camino. */
    event.preventDefault();

    if (is_disabled) {
      setTouched({
        name: true,
        email: true,
        phone: true,
        industry: true,
        message: true,
      });
      return;
    }

    const payload = {
      name: state.name.trim(),
      last_name: FILLER.last_name,
      job: FILLER.job,
      email: state.email.trim(),
      phone: is_satty ? state.phone.trim() : FILLER.phone,
      message: state.message.trim(),
      /* El backend ya arma el correo con `str_options`, así que la industria
         viaja también ahí: llega al buzón aunque `industry` se ignore. */
      str_options: is_satty ? state.industry : FILLER.str_options,
      options: FILLER.options,
    };
    if (is_satty) {
      payload.industry = state.industry;
      /* El backend elige con esto la plantilla y el PDF de SATTY */
      payload.product = 'satty';
    }
    if (captcha_token) {
      payload.turnstile_token = captcha_token;
    }

    setSending(true);
    try {
      await axios({ method: 'POST', url: ENDPOINT, data: payload });
      if (form_ref.current) form_ref.current.reset();
      setState({ name: '', email: '', phone: '', industry: '', message: '' });
      setTouched({});
      alert.show('Formulario enviado!');
    } catch (e) {
      alert.show('Error enviando formulario! Intentelo mas tarde');
    } finally {
      setSending(false);
      /* El token es de un solo uso: haya salido bien o mal, el próximo envío
         necesita uno nuevo. */
      setCaptchaToken('');
      if (turnstile_ref.current) turnstile_ref.current.reset();
    }
  };

  return (
    <div className="contact-box text-center">
      <form ref={form_ref} onSubmit={submitHandler} className="contact-form" noValidate>
        <div className="row contact-form-container">
          <div className="col-12">
            <div className="form-group">
              <input
                type="text"
                className={fieldClass('name')}
                name="name"
                placeholder={placeholder.name}
                value={state.name}
                onChange={changeHandler}
                onBlur={blurHandler}
              />
              {fieldError('name')}
            </div>
          </div>

          <div className={is_satty ? 'col-12 col-md-6' : 'col-12'}>
            <div className="form-group">
              <input
                type="email"
                className={fieldClass('email')}
                name="email"
                placeholder={placeholder.email}
                value={state.email}
                onChange={changeHandler}
                onBlur={blurHandler}
              />
              {fieldError('email')}
            </div>
          </div>

          {is_satty && (
            <div className="col-12 col-md-6">
              <div className="form-group">
                <input
                  type="tel"
                  className={fieldClass('phone')}
                  name="phone"
                  placeholder={placeholder.phone}
                  value={state.phone}
                  onChange={changeHandler}
                  onBlur={blurHandler}
                />
                {fieldError('phone')}
              </div>
            </div>
          )}

          {is_satty && (
            <div className="col-12">
              <div className="form-group">
                <select
                  className={fieldClass('industry')}
                  name="industry"
                  value={state.industry}
                  onChange={changeHandler}
                  onBlur={blurHandler}
                  aria-label={t('contact.satty.industry_label')}
                >
                  <option value="">{placeholder.industry}</option>
                  {industries.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                {fieldError('industry')}
              </div>
            </div>
          )}

          <div className="col-12">
            <div className="form-group">
              <textarea
                className={fieldClass('message')}
                name="message"
                placeholder={placeholder.msg}
                value={state.message}
                onChange={changeHandler}
                onBlur={blurHandler}
              />
              {fieldError('message')}
            </div>
          </div>

          {needs_captcha && (
            <div className="col-12">
              <div className="form-group d-flex flex-column align-items-center">
                <TurnstileWidget
                  ref={turnstile_ref}
                  siteKey={TURNSTILE_SITE_KEY}
                  onVerify={(token) => {
                    setCaptchaToken(token);
                    setCaptchaFailed(false);
                  }}
                  onExpire={() => setCaptchaToken('')}
                  onError={() => {
                    setCaptchaToken('');
                    setCaptchaFailed(true);
                  }}
                />
                {captcha_failed && (
                  <small className="text-danger mt-2">{t('contact.captcha.error')}</small>
                )}
              </div>
            </div>
          )}

          <div className="col-12">
            <button
              type="submit"
              style={{ background: props.data.color }}
              className="btn btn-lg btn-block mt-3"
              disabled={is_disabled}
            >
              {sending ? <Spinner /> : <Send />}
              {sending ? placeholder.sent : placeholder.button}
            </button>
            {needs_captcha && !captcha_ready && !captcha_failed && !has_errors && (
              <small className="text-muted d-block mt-2">
                {t('contact.captcha.pending')}
              </small>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default withTranslation('common')(ContactForm);
